/* =============================================================================
 * Search Drawer (ksrgqh2s) — Frido
 *
 * Top-anchored predictive search overlay using the Spectrum.search storefront
 * SDK.
 *
 * Behaviour:
 *   1. Trigger button opens a top-anchored dialog (desktop) / full-screen
 *      overlay (mobile).
 *   2. Empty input shows Recent Searches (text chips) + Popular Searches
 *      (server-rendered from the merchant-configured inline-list prop) with an
 *      animated rotating placeholder.
 *   3. Typing (non-empty input) switches to Top Suggestions — a single-column
 *      list of product rows with the matched query substring bolded — plus a
 *      "View All" link. Powered by Spectrum.search.predictive (200ms debounce).
 *   4. Clearing back to empty returns to the initial view. The initial <->
 *      typing switch is driven purely by input CONTENT, never by focus.
 *   5. Enter navigates to the configurable search results page.
 *
 * Popular Searches is rendered by Liquid at SSR (no fetch here). This IIFE
 * never fetches a Shopify collection.
 *
 * SDK dependencies (resolved lazily at call time, not at init):
 *   - Spectrum.search  (predictive, autoComplete, getRecent, addRecent,
 *                       removeRecent, clearRecent)
 *   - Spectrum.money   (format)
 * ============================================================================= */

;(() => {
  if (window.__sai_ksrgqh2s_initialized__) return
  window.__sai_ksrgqh2s_initialized__ = true

  const SNIPPET_ID = 'ksrgqh2s'
  const TAG = 'sai-ksrgqh2s'
  const FEATURE_SLUG = 'search_drawer'
  const DEBOUNCE_MS = 200

  // Min query length before requesting typeahead suggestions.
  const AUTOCOMPLETE_MIN_LENGTH = 2

  function noopTrack() {}

  function searchApi() {
    return window.Spectrum?.search ?? null
  }

  function formatMoney(cents) {
    try {
      return window.Spectrum.money.format(cents)
    } catch (_) {
      return ''
    }
  }

  // Leading search icon for a recent-search chip.
  const CHIP_SEARCH_ICON =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>'

  const CHIP_X_ICON =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'

  // Thumbnail placeholder for suggestion rows with no product image.
  const SUGG_IMG_PLACEHOLDER =
    '<span class="sai-ksrgqh2s__srow-img sai-ksrgqh2s__srow-img--empty"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></span>'

  // Escape a string for safe use inside an HTML attribute value.
  function attrEscape(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  // Escape a string for safe use as HTML text content.
  function textEscape(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  // Bold the first case-insensitive occurrence of `q` inside `name`. Returns an
  // HTML string with the matched span wrapped in <b>; the surrounding text and
  // the query are HTML-escaped so product titles can't inject markup.
  function boldMatch(name, q) {
    const safeName = textEscape(name)
    if (!q) return safeName
    const i = String(name).toLowerCase().indexOf(String(q).toLowerCase())
    if (i < 0) return safeName
    const before = textEscape(String(name).slice(0, i))
    const hit = textEscape(String(name).slice(i, i + q.length))
    const after = textEscape(String(name).slice(i + q.length))
    return `${before}<b>${hit}</b>${after}`
  }

  function buildRecentChip(query, showDelete) {
    const del = showDelete
      ? `<button class="sai-ksrgqh2s__chip-x" type="button" data-delete-query="${attrEscape(query)}" aria-label="Remove">${CHIP_X_ICON}</button>`
      : ''
    return `<div class="sai-ksrgqh2s__chip" data-query="${attrEscape(query)}"><span class="sai-ksrgqh2s__chip-ico">${CHIP_SEARCH_ICON}</span><span class="sai-ksrgqh2s__chip-nm">${textEscape(query)}</span>${del}</div>`
  }

  function buildSuggestionRow(product, query) {
    const img = product.featured_image || product.images?.[0] || ''
    const title = product.title || ''
    const url = product.url || `/products/${product.handle || ''}`
    const imgHtml = img
      ? `<img class="sai-ksrgqh2s__srow-img" src="${attrEscape(img)}" alt="" loading="lazy">`
      : SUGG_IMG_PLACEHOLDER
    return `<a class="sai-ksrgqh2s__srow" href="${attrEscape(url)}"><span class="sai-ksrgqh2s__srow-img-wrap">${imgHtml}</span><span class="sai-ksrgqh2s__srow-nm">${boldMatch(title, query)}</span></a>`
  }

  function buildSkeletonRow() {
    return `<div class="sai-ksrgqh2s__skeleton-row"><div class="sai-ksrgqh2s__skeleton-img"></div><div class="sai-ksrgqh2s__skeleton-line"></div></div>`
  }

  function buildSkeletonList(count) {
    return Array.from({ length: count }, buildSkeletonRow).join('')
  }

  if (!customElements.get(TAG)) {
    class SaiSearchDrawer extends HTMLElement {
      connectedCallback() {
        // A system-experience search drawer is an always-visible base-layer
        // snippet. Its wrapper SSRs data-spectrum-vis="off" for live and relies
        // on the storefront SDK to flip it "on"; that flip does not re-run when
        // the theme morphs the header on cart events, which reset the wrapper to
        // "off" and hid the trigger. Pin vis="on" on our own wrapper (system
        // experiences only) and keep it pinned across in-place attribute resets.
        this._ensureVisible()
        if (this._initialized) return
        this._initialized = true
        requestAnimationFrame(() => this._init())
      }

      _ensureVisible() {
        const wrap = this.closest('[data-spectrum-lq-snippet]')
        if (!wrap) return
        const exp = wrap.getAttribute('data-spectrum-experience') || ''
        if (!exp.startsWith('_system-')) return
        const pin = () => {
          if (wrap.getAttribute('data-spectrum-vis') !== 'on') {
            wrap.setAttribute('data-spectrum-vis', 'on')
          }
        }
        pin()
        if (!this._visObserver) {
          this._visObserver = new MutationObserver(pin)
          this._visObserver.observe(wrap, {
            attributes: true,
            attributeFilter: ['data-spectrum-vis'],
          })
        }
      }

      _init() {
        this._track = noopTrack
        this._debounceTimer = null
        this._lastQuery = ''
        this._searchSeq = 0

        this._els = {
          trigger: this.querySelector('.sai-ksrgqh2s__trigger'),
          backdrop: this.querySelector('.sai-ksrgqh2s__backdrop'),
          panel: this.querySelector('.sai-ksrgqh2s__panel'),
          input: this.querySelector('.sai-ksrgqh2s__input'),
          clearBtn: this.querySelector('.sai-ksrgqh2s__clear'),
          cancelBtn: this.querySelector('.sai-ksrgqh2s__cancel'),
          mCloseBtn: this.querySelector('.sai-ksrgqh2s__mclose'),
          initial: this.querySelector('.sai-ksrgqh2s__initial'),
          typing: this.querySelector('.sai-ksrgqh2s__typing'),
          recent: this.querySelector('.sai-ksrgqh2s__recent'),
          results: this.querySelector('.sai-ksrgqh2s__results'),
          sugg: this.querySelector('.sai-ksrgqh2s__sugg'),
          viewAll: this.querySelector('.sai-ksrgqh2s__viewall'),
          viewAllText: this.querySelector('.sai-ksrgqh2s__viewall-text'),
          noResults: this.querySelector('.sai-ksrgqh2s__no-results'),
          loading: this.querySelector('.sai-ksrgqh2s__loading'),
          loadingList: this.querySelector('.sai-ksrgqh2s__loading-list'),
        }

        if (!this._els.trigger) return

        this._placeholderPrefix = this.getAttribute('data-placeholder-prefix') ?? 'Search for'
        this._placeholderSuggestions = (this.getAttribute('data-placeholder-suggestions') || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        this._placeholderAnimation = this.getAttribute('data-placeholder-animation') || 'rotate'
        this._placeholderAnimTimer = null
        this._placeholderIdx = 0
        this._searchPageUrl = this.getAttribute('data-search-page-url') || '/search'
        this._searchIconPosition = this.getAttribute('data-search-icon-position') || 'left'
        this._showClearButton = this.getAttribute('data-show-clear-button') !== 'false'
        this._autoFocus = this.getAttribute('data-auto-focus') !== 'false'
        this._showResultsPreview = this.getAttribute('data-show-results-preview') !== 'false'
        this._resultsCount = Number(this.getAttribute('data-results-count')) || 8
        this._viewAllText = this.getAttribute('data-view-all-text') || 'View All'
        this._showRecent = this.getAttribute('data-show-recent') !== 'false'
        this._recentMaxItems = Number(this.getAttribute('data-recent-max-items')) || 8
        this._showClearAll = this.getAttribute('data-show-clear-all') !== 'false'
        this._showIndividualDelete = this.getAttribute('data-show-individual-delete') !== 'false'
        this._panelOffsetSelector = this.getAttribute('data-panel-offset-selector') || ''
        this._panelOffsetExtra = Number(this.getAttribute('data-panel-offset-extra')) || 0

        this._bindEvents()
      }

      setAnalytics(track) {
        this._track = typeof track === 'function' ? track : noopTrack
      }

      _bindEvents() {
        const els = this._els

        els.trigger.addEventListener('click', () => this._open())

        if (els.cancelBtn) els.cancelBtn.addEventListener('click', () => this._close())
        if (els.mCloseBtn) els.mCloseBtn.addEventListener('click', () => this._close())

        els.backdrop.addEventListener('click', (e) => {
          if (e.target === els.backdrop) this._close()
        })

        if (els.clearBtn) {
          els.clearBtn.addEventListener('click', () => {
            els.input.value = ''
            els.input.classList.remove('sai-ksrgqh2s__input--has-text')
            els.clearBtn.hidden = true
            this._onInputChange()
            els.input.focus()
          })
        }

        // Clicking anywhere in the bar just focuses the input — it does NOT
        // switch to suggestions. The initial <-> typing switch is content-driven.
        const bar = this.querySelector('.sai-ksrgqh2s__bar')
        if (bar) bar.addEventListener('click', () => els.input.focus())

        els.input.addEventListener('input', () => {
          const hasText = !!els.input.value.trim()
          els.input.classList.toggle('sai-ksrgqh2s__input--has-text', hasText)
          // Hide the animated placeholder synchronously on the first keystroke,
          // and resume it the moment the field is cleared back to empty.
          // Leaving this to the debounced search made the placeholder linger
          // over the typed text, and never brought it back after clearing.
          if (els.input.value) this._stopPlaceholderAnim()
          else this._startPlaceholderAnim()
          if (els.clearBtn) els.clearBtn.hidden = !els.input.value
          this._scheduleSearch()
        })

        els.input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            this._close()
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            const q = (els.input.value || '').trim()
            if (q) {
              this._track(`${FEATURE_SLUG}:submit_elem_clicked`, { query: q })
              const api = searchApi()
              if (api) api.addRecent(q)
              this._close()
              window.location.href = `${this._searchPageUrl}?q=${encodeURIComponent(q)}`
            }
          }
        })

        els.backdrop.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this._close()
        })

        if (els.sugg) {
          els.sugg.addEventListener('click', (e) => {
            const row = e.target.closest('.sai-ksrgqh2s__srow')
            if (row) {
              this._track(`${FEATURE_SLUG}:product_click`, {
                query: this._lastQuery,
                product_url: row.getAttribute('href') || '',
                product_title: row.querySelector('.sai-ksrgqh2s__srow-nm')?.textContent || '',
              })
            }
          })
        }
      }

      // Measure the configured offset element's bottom edge and pin the panel
      // top to it (desktop only). Mobile is full-screen — offset forced to 0 in
      // CSS. Falls back to the extra value alone when no selector resolves.
      _applyPanelOffset() {
        // The backdrop is relocated to document.body on open, so the offset
        // custom property must be set on the backdrop itself — not the host —
        // for the panel's `top: var(--sai-ksr-offset)` to resolve.
        const bd = this._els.backdrop
        // Mobile is a top sheet pinned to the very top — no announcement offset.
        if (window.innerWidth <= 640) {
          bd.style.setProperty('--sai-ksr-offset', '0px')
          return
        }
        let base = 0
        if (this._panelOffsetSelector) {
          const el = document.querySelector(this._panelOffsetSelector)
          if (el) base = el.getBoundingClientRect().bottom
        }
        bd.style.setProperty('--sai-ksr-offset', `${base + this._panelOffsetExtra}px`)
      }

      _open() {
        if (this._isOpen) return
        this._isOpen = true
        const els = this._els

        if (!this._movedToBody) {
          document.body.appendChild(els.backdrop)
          this._movedToBody = true
        }

        this._applyPanelOffset()
        els.backdrop.hidden = false
        els.backdrop.classList.remove('sai-ksrgqh2s__backdrop--closing')
        document.body.style.overflow = 'hidden'

        els.input.value = ''
        els.input.classList.remove('sai-ksrgqh2s__input--has-text')
        if (els.clearBtn) els.clearBtn.hidden = true
        this._showInitialState()

        void els.backdrop.offsetHeight
        els.backdrop.classList.add('sai-ksrgqh2s__backdrop--visible')
        if (this._autoFocus) els.input.focus()
        setTimeout(() => this._startPlaceholderAnim(), 0)
        this._track(`${FEATURE_SLUG}:open_elem_clicked`, {})
      }

      _close() {
        if (!this._isOpen) return
        this._isOpen = false
        this._stopPlaceholderAnim()
        const els = this._els
        const query = this._lastQuery
        els.backdrop.classList.remove('sai-ksrgqh2s__backdrop--visible')
        els.backdrop.classList.add('sai-ksrgqh2s__backdrop--closing')
        if (this._debounceTimer) {
          clearTimeout(this._debounceTimer)
          this._debounceTimer = null
        }
        const onEnd = () => {
          els.backdrop.removeEventListener('transitionend', onEnd)
          els.backdrop.classList.remove('sai-ksrgqh2s__backdrop--closing')
          els.backdrop.hidden = true
          document.body.style.overflow = ''
          this._showInitialState()
        }
        els.backdrop.addEventListener('transitionend', onEnd, { once: true })
        setTimeout(onEnd, 300)
        if (query) this._track(`${FEATURE_SLUG}:close_elem_clicked`, { query })
        this._lastQuery = ''
      }

      // Empty input → initial view (Recent + server-rendered Popular).
      _showInitialState() {
        const els = this._els
        ++this._searchSeq
        els.typing.hidden = true
        els.initial.hidden = false
        if (els.results) els.results.hidden = true
        if (els.loading) els.loading.hidden = true
        if (els.noResults) els.noResults.hidden = true
        this._renderRecent()
        // Returning to the initial view (open, cleared input, empty search)
        // always resumes the animated placeholder + blinking cursor.
        if (!els.input.value) this._startPlaceholderAnim()
      }

      // Non-empty input → typing view (suggestions).
      _showTypingState() {
        const els = this._els
        els.initial.hidden = true
        els.typing.hidden = false
      }

      _renderRecent() {
        if (!this._showRecent) return
        const els = this._els
        if (!els.recent) return
        const api = searchApi()
        if (!api) {
          els.recent.hidden = true
          return
        }

        const recent = api.getRecent()
        if (!recent || recent.length === 0) {
          els.recent.hidden = true
          return
        }

        const clearHtml = this._showClearAll
          ? `<button class="sai-ksrgqh2s__clear-all" type="button">Clear All</button>`
          : ''
        const chips = recent
          .slice(0, this._recentMaxItems)
          .map((q) => buildRecentChip(q, this._showIndividualDelete))
          .join('')
        els.recent.innerHTML =
          `<div class="sai-ksrgqh2s__sec-head"><h3 class="sai-ksrgqh2s__h">${textEscape(this._recentTitleText())}</h3>${clearHtml}</div>` +
          `<div class="sai-ksrgqh2s__recent-row">${chips}</div>`
        els.recent.hidden = false

        const clearBtn = els.recent.querySelector('.sai-ksrgqh2s__clear-all')
        if (clearBtn) {
          clearBtn.addEventListener(
            'click',
            () => {
              api.clearRecent()
              els.recent.hidden = true
            },
            { once: true },
          )
        }

        for (const delBtn of els.recent.querySelectorAll('.sai-ksrgqh2s__chip-x')) {
          delBtn.addEventListener('click', (e) => {
            e.stopPropagation()
            const q = delBtn.getAttribute('data-delete-query')
            if (q && api.removeRecent) {
              api.removeRecent(q)
            } else if (q) {
              const all = api.getRecent() || []
              api.clearRecent()
              for (const r of all.filter((r) => r !== q)) api.addRecent(r)
            }
            delBtn.closest('.sai-ksrgqh2s__chip').remove()
            if (!els.recent.querySelector('.sai-ksrgqh2s__chip')) els.recent.hidden = true
          })
        }

        for (const chip of els.recent.querySelectorAll('.sai-ksrgqh2s__chip')) {
          chip.addEventListener('click', () => {
            const q = chip.getAttribute('data-query')
            if (q) {
              els.input.value = q
              els.input.classList.add('sai-ksrgqh2s__input--has-text')
              if (els.clearBtn) els.clearBtn.hidden = false
              this._lastQuery = q
              this._stopPlaceholderAnim()
              this._doSearch(q)
            }
          })
        }
      }

      _recentTitleText() {
        // Heading text lives on the SSR markup; recent block is JS-rendered so
        // we mirror the merchant-facing title via a data attribute fallback.
        return this.getAttribute('data-recent-title') || 'Recent Searches'
      }

      _scheduleSearch() {
        if (this._debounceTimer) clearTimeout(this._debounceTimer)
        this._debounceTimer = setTimeout(() => this._onInputChange(), DEBOUNCE_MS)
      }

      _onInputChange() {
        const query = (this._els.input.value || '').trim()
        if (!query) {
          this._lastQuery = ''
          this._showInitialState()
          return
        }
        this._lastQuery = query
        if (this._showResultsPreview) {
          this._showTypingState()
          this._doSearch(query)
        }
      }

      async _doSearch(query) {
        const els = this._els
        const api = searchApi()

        this._showTypingState()

        if (!api) {
          els.results.hidden = true
          els.loading.hidden = true
          els.noResults.textContent = 'Search is not available. Please try again later.'
          els.noResults.hidden = false
          return
        }

        const searchId = ++this._searchSeq
        els.results.hidden = true
        els.noResults.hidden = true
        els.loadingList.innerHTML = buildSkeletonList(Math.min(this._resultsCount, 6))
        els.loading.hidden = false

        const searchResult = await api.predictive(query, { hitsPerPage: this._resultsCount })
        if (searchResult === null || searchId !== this._searchSeq) return

        els.loading.hidden = true

        if (!searchResult.ok || !searchResult.data) {
          els.results.hidden = true
          els.noResults.textContent = 'Something went wrong. Try again.'
          els.noResults.hidden = false
          return
        }

        const products = searchResult.data.products || []
        const totalHits = searchResult.data.totalHits || 0

        if (products.length === 0) {
          els.results.hidden = true
          els.noResults.textContent = `No results found for "${query}". Try another search.`
          els.noResults.hidden = false
          this._track(`${FEATURE_SLUG}:no_results`, { query })
          return
        }

        api.addRecent(query)

        els.sugg.innerHTML = products
          .slice(0, this._resultsCount)
          .map((p) => buildSuggestionRow(p, query))
          .join('')

        const count = totalHits || products.length
        els.viewAll.href = `${this._searchPageUrl}?q=${encodeURIComponent(query)}`
        if (els.viewAllText) {
          els.viewAllText.textContent = `${this._viewAllText} "${count}" Products`
        }

        els.results.hidden = false
        this._track(`${FEATURE_SLUG}:results`, {
          query,
          result_count: products.length,
          total_hits: totalHits,
        })
      }

      _ensurePlaceholderOverlay() {
        if (this._placeholderOverlay && this._placeholderOverlayPrefix === this._placeholderPrefix)
          return
        if (this._placeholderOverlay) {
          this._placeholderOverlay.remove()
          this._placeholderOverlay = null
          this._placeholderSlot = null
        }
        const wrap = this._els.input.parentElement
        for (const el of wrap.querySelectorAll('.sai-ksrgqh2s__placeholder-anim')) el.remove()
        const overlay = document.createElement('div')
        overlay.className = 'sai-ksrgqh2s__placeholder-anim'
        const prefixHtml = this._placeholderPrefix
          ? `<span class="sai-ksrgqh2s__placeholder-prefix">${textEscape(this._placeholderPrefix)}</span>`
          : ''
        // Blinking cursor sits before the prefix (matches the harness).
        overlay.innerHTML = `<span class="sai-ksrgqh2s__placeholder-cursor"></span>${prefixHtml}<span class="sai-ksrgqh2s__placeholder-slot"></span>`
        wrap.insertBefore(overlay, this._els.input)
        this._placeholderOverlay = overlay
        this._placeholderOverlayPrefix = this._placeholderPrefix
        this._placeholderSlot = overlay.querySelector('.sai-ksrgqh2s__placeholder-slot')
      }

      _startPlaceholderAnim() {
        if (this._placeholderSuggestions.length === 0) return
        if (this._els.input.value) return
        this._stopPlaceholderAnim()
        this._ensurePlaceholderOverlay()
        this._placeholderOverlay.hidden = false
        this._placeholderIdx = 0
        this._rotatePlaceholder()
      }

      _stopPlaceholderAnim() {
        if (this._placeholderAnimTimer) {
          clearInterval(this._placeholderAnimTimer)
          this._placeholderAnimTimer = null
        }
        if (this._placeholderOverlay) this._placeholderOverlay.hidden = true
      }

      _rotatePlaceholder() {
        const slot = this._placeholderSlot
        const suggestions = this._placeholderSuggestions
        if (!slot || suggestions.length === 0) return

        const wordHtml = (w) => `<b>'${textEscape(w)}'</b>`

        const showWord = (immediate) => {
          if (this._els.input.value) {
            this._placeholderOverlay.hidden = true
            return
          }
          this._placeholderOverlay.hidden = false
          const word = suggestions[this._placeholderIdx % suggestions.length]

          if (immediate) {
            slot.innerHTML = wordHtml(word)
            slot.classList.remove('sai-ksrgqh2s__placeholder-slot--out')
            slot.classList.add('sai-ksrgqh2s__placeholder-slot--in')
            this._placeholderIdx++
            return
          }

          slot.classList.remove(
            'sai-ksrgqh2s__placeholder-slot--in',
            'sai-ksrgqh2s__placeholder-slot--enter',
          )
          slot.classList.add('sai-ksrgqh2s__placeholder-slot--out')

          setTimeout(() => {
            slot.classList.remove('sai-ksrgqh2s__placeholder-slot--out')
            slot.classList.add('sai-ksrgqh2s__placeholder-slot--enter')
            slot.innerHTML = wordHtml(word)
            void slot.offsetHeight
            slot.classList.remove('sai-ksrgqh2s__placeholder-slot--enter')
            slot.classList.add('sai-ksrgqh2s__placeholder-slot--in')
            this._placeholderIdx++
          }, 380)
        }

        showWord(true)
        this._placeholderAnimTimer = setInterval(() => showWord(false), 2200)
      }

      applyVariant(content) {
        if (!this._els) return
        this._els.input.placeholder = content?.placeholder || 'Search...'
        this._placeholderPrefix = content?.placeholder_prefix ?? 'Search for'
        this._placeholderAnimation = content?.placeholder_animation || 'rotate'
        this._placeholderSuggestions = (content?.placeholder_suggestions || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        this._searchPageUrl = content?.search_page_url || '/search'
        this._searchIconPosition = content?.search_icon_position || 'left'
        this._showClearButton = content?.show_clear_button !== false
        this._autoFocus = content?.auto_focus !== false
        this._showResultsPreview = content?.show_results_preview !== false
        this._resultsCount = content?.results_count || 8
        this._viewAllText = content?.view_all_text || 'View All'
        this._showRecent = content?.show_recent !== false
        this._recentMaxItems = content?.recent_max_items || 8
        this._showClearAll = content?.show_clear_all !== false
        this._showIndividualDelete = content?.show_individual_delete !== false
        this._panelOffsetSelector = content?.panel_offset_selector || ''
        this._panelOffsetExtra = Number(content?.panel_offset_extra) || 0
      }
    }

    customElements.define(TAG, SaiSearchDrawer)
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) return

    for (const node of containers) {
      const handles = api.bind(node, ({ variant }) => {
        if (!variant?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(variant.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
